// Asbestos Surveys — a small building-level list of full survey report files
// (separate from the floor/zone register + its own PDF export). Anyone with
// access to the building can view/download; upload + delete are PR-only (canEdit).
import { useRef, useState } from "react";
import { FileText, Upload, Loader2, Trash2, Eye, Download, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

function formatBytes(n) {
    if (!n) return "0 KB";
    if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDate(d) {
    if (!d) return null;
    try { return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
    catch { return d; }
}

export default function AsbestosSurveysPanel({ buildingId, canEdit, surveys, onRefresh }) {
    const [addOpen, setAddOpen] = useState(false);
    const [title, setTitle] = useState("");
    const [surveyDate, setSurveyDate] = useState("");
    const [uploading, setUploading] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const fileRef = useRef(null);

    function openFilePicker() {
        if (!title.trim()) { toast.error("Give the survey a title first"); return; }
        fileRef.current?.click();
    }

    async function onPickFile(e) {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        fd.append("title", title.trim());
        if (surveyDate) fd.append("survey_date", surveyDate);
        setUploading(true);
        try {
            await api.post(`/asbestos/building/${buildingId}/surveys`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Survey uploaded");
            setAddOpen(false); setTitle(""); setSurveyDate("");
            onRefresh?.();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
        } finally {
            setUploading(false);
        }
    }

    async function viewSurvey(s) {
        setBusyId(s.id);
        try {
            const res = await api.get(`/asbestos/surveys/${s.id}/file`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            window.open(url, "_blank");
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not open survey");
        } finally {
            setBusyId(null);
        }
    }

    async function downloadSurvey(s) {
        setBusyId(s.id);
        try {
            const res = await api.get(`/asbestos/surveys/${s.id}/file`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url; a.download = s.filename || `${s.title}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not download survey");
        } finally {
            setBusyId(null);
        }
    }

    async function deleteSurvey(s) {
        if (!window.confirm(`Delete "${s.title}"? This cannot be undone.`)) return;
        setBusyId(s.id);
        try {
            await api.delete(`/asbestos/surveys/${s.id}`);
            toast.success("Survey deleted");
            onRefresh?.();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
        } finally {
            setBusyId(null);
        }
    }

    return (
        <div data-testid="asbestos-surveys-panel" className="space-y-2">
            {surveys.length === 0 ? (
                <div className="text-[12px] italic rounded-[10px] border border-dashed p-4" style={{ color: "#9A968F", borderColor: "#E2DDD5" }}>
                    No surveys uploaded yet.{canEdit ? " Add the full survey report PDF for this building." : ""}
                </div>
            ) : surveys.map((s) => (
                <div key={s.id} data-testid={`asbestos-survey-${s.id}`}
                    className="rounded-[10px] border px-3 py-2.5 bg-white" style={{ borderColor: "#EFEBE4" }}>
                    <div className="flex items-start gap-2">
                        <FileText className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: "#2A5DB0" }} />
                        <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-semibold text-[#1F1F1F] truncate" title={s.title}>{s.title}</div>
                            <div className="text-[10.5px] mt-0.5" style={{ color: "#9A968F" }}>
                                {fmtDate(s.survey_date) ? `Survey date: ${fmtDate(s.survey_date)} · ` : ""}{formatBytes(s.size)}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-1.5 mt-2">
                        <button data-testid={`asbestos-survey-view-${s.id}`} disabled={busyId === s.id} onClick={() => viewSurvey(s)}
                            className="flex-1 inline-flex items-center justify-center gap-1 text-[11px] font-semibold py-1.5 rounded-[6px] border disabled:opacity-50" style={{ borderColor: "#E2DDD5", color: "#1F1F1F" }}>
                            {busyId === s.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />} View
                        </button>
                        <button data-testid={`asbestos-survey-download-${s.id}`} disabled={busyId === s.id} onClick={() => downloadSurvey(s)}
                            className="flex-1 inline-flex items-center justify-center gap-1 text-[11px] font-semibold py-1.5 rounded-[6px] border disabled:opacity-50" style={{ borderColor: "#E2DDD5", color: "#1F1F1F" }}>
                            <Download className="w-3 h-3" /> Download
                        </button>
                        {canEdit && (
                            <button data-testid={`asbestos-survey-delete-${s.id}`} disabled={busyId === s.id} onClick={() => deleteSurvey(s)} title="Delete"
                                className="w-7 h-7 shrink-0 grid place-items-center rounded-[6px] hover:bg-[#FBE8E5]">
                                <Trash2 className="w-3.5 h-3.5" style={{ color: "#B33A2E" }} />
                            </button>
                        )}
                    </div>
                </div>
            ))}

            {canEdit && (
                addOpen ? (
                    <div className="rounded-[10px] border p-2.5 space-y-2" style={{ borderColor: "#E2DDD5", background: "#FCFBF9" }}>
                        <div className="flex items-center justify-between">
                            <span className="text-[10.5px] font-bold uppercase tracking-wider" style={{ color: "#9A968F" }}>New survey</span>
                            <button onClick={() => { setAddOpen(false); setTitle(""); setSurveyDate(""); }}><X className="w-3.5 h-3.5" style={{ color: "#9A968F" }} /></button>
                        </div>
                        <input data-testid="asbestos-survey-title" value={title} onChange={(e) => setTitle(e.target.value)}
                            placeholder="Survey title (e.g. Management Survey — 2026)"
                            className="w-full text-[12px] rounded-[7px] border px-2 py-1.5 bg-white" style={{ borderColor: "#E2DDD5" }} />
                        <input data-testid="asbestos-survey-date" type="date" value={surveyDate} onChange={(e) => setSurveyDate(e.target.value)}
                            className="w-full text-[12px] rounded-[7px] border px-2 py-1.5 bg-white" style={{ borderColor: "#E2DDD5" }} />
                        <input ref={fileRef} type="file" accept="application/pdf,image/png,image/jpeg,image/webp" hidden onChange={onPickFile} data-testid="asbestos-survey-file-input" />
                        <button data-testid="asbestos-survey-choose-file" disabled={uploading} onClick={openFilePicker}
                            className="w-full inline-flex items-center justify-center gap-1.5 text-[12px] font-semibold py-1.5 rounded-[7px] text-white disabled:opacity-60" style={{ background: "#C8632A" }}>
                            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Choose file &amp; upload
                        </button>
                    </div>
                ) : (
                    <button data-testid="asbestos-survey-add-btn" onClick={() => setAddOpen(true)}
                        className="w-full inline-flex items-center justify-center gap-1.5 text-[11.5px] font-semibold py-1.5 rounded-[7px]" style={{ color: "#C8632A", background: "#FBEDE6" }}>
                        <Plus className="w-3.5 h-3.5" /> Add survey
                    </button>
                )
            )}
        </div>
    );
}
